import { useMemo } from 'react';
import useSWR from "swr";
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { fetcher } from '../utils/fetcher';

interface ApiCategory extends Category { }

interface CategoryApiResponse {
  data: ApiCategory[];
}

enum CATEGORY_TYPE {
  EXPENSE = '*',
  INCOME = 'Income',
  OTHER = 'Others'
}

type CategoryByType = {
  expense: Category[];
  income: Category[];
  other: Category[];
}


const groupByCategoryType = (categories: Category[]): CategoryByType => {
  return categories.reduce((acc: CategoryByType, category: Category): CategoryByType => {
    switch (category.name?.toLowerCase()) {
      case CATEGORY_TYPE.INCOME.toLowerCase():
        acc.income.push(category);
        break;
      case CATEGORY_TYPE.OTHER.toLowerCase():
        acc.other.push(category);
        break;
      default:
        acc.expense.push(category);
        break;
    }

    return acc;
  }, { expense: [], income: [], other: [] } as CategoryByType);

}

/**
 * Hook to manage category data
 * @returns Category data and helper functions
 */
export function useCategories(cashFlow?: CategoryType) {
  const endpoint = useMemo(() => {
    switch (cashFlow) {
      case 'expense':
        return API_ENDPOINTS.EXPENSE_CATEGORY;
      case 'income':
        return API_ENDPOINTS.INCOME_CATEGORY;
      default:
        return API_ENDPOINTS.ALL_CATEGORIES;
    }
  }, [cashFlow]);

  const swrOptions = useMemo(() => ({
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  }), []);

  const { data, isLoading, error, mutate } = useSWR<CategoryApiResponse>(
    endpoint,
    fetcher,
    swrOptions
  );

  const categories = useMemo(() => {
    return data?.data?.map((category) => {
      const categories = category.categories;

      return {
        ...category,
        isRoot: true,
        categories: categories ? categories.map((cat) => ({
          ...cat,
          parent: category,
        })) : [],
      }
    });
  }, [data?.data]);

  const categoriesGroupedByType = useMemo(() => {
    return categories ? groupByCategoryType(categories as Category[]) : undefined;
  }, [categories]);

  const getCategoryById = useMemo(() => {
    return (id: string): Category | undefined => {
      if (!categories) return undefined;

      const findCategory = (categoryList: Category[]): Category | undefined => {
        for (const category of categoryList) {
          if (category.id === id) {
            return category;
          }
          if (category.categories) {
            const found = findCategory(category.categories);
            if (found) return found;
          }
        }
        return undefined;
      };

      return findCategory(categories);
    };
  }, [categories]);

  return useMemo(() => ({
    data: categories,
    categoriesGroupedByType,
    expenseCategories: categoriesGroupedByType?.expense,
    incomeCategories: categoriesGroupedByType?.income,
    otherCategories: categoriesGroupedByType?.other,
    isLoading,
    error,
    mutate,
    getCategoryById,
  }), [categories, categoriesGroupedByType, isLoading, error, mutate, getCategoryById]);
}

export default useCategories; 